"use client";

import React, { useEffect, useRef, useState } from "react";

interface LazyRevealProps {
  children: React.ReactNode;
  shimmer?: React.ReactNode; // shimmer/spinner placeholder
  rootMargin?: string;
  threshold?: number | number[];
  className?: string; // wrapper styling
  onVisible?: () => void; // callback when component becomes visible
  id?: string;
  style?: React.CSSProperties;
}

const DefaultShimmer = () => (
  <div className="animate-pulse bg-gray-300 h-6 w-full rounded-lg" />
);

export default function LazyReveal({
  children,
  shimmer,
  rootMargin = "0px",
  threshold = 0.1,
  className,
  onVisible,
  id,
  style
}: LazyRevealProps) {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!wrapperRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setVisible(true);
          onVisible && onVisible();
          observerRef.current?.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observerRef.current.observe(wrapperRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rootMargin, threshold]);

  return (
    <div ref={wrapperRef} className={className} id={id} style={style}>
      {visible ? children : (shimmer || <DefaultShimmer />)}
    </div>
  );
}
